/* import __COLOCATED_TEMPLATE__ from './merge-into.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-experience */
import { action } from '@ember/object';
import Component from '@glimmer/component';
import type { ConversationRecord } from 'embercom/objects/inbox/types/conversation-record';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import type Snackbar from 'embercom/services/snackbar';
import type UserSummary from 'embercom/objects/inbox/user-summary';
import type CommandKService from 'embercom/services/command-k';
import type Session from 'embercom/services/session';
import { tracked } from '@glimmer/tracking';

interface Args {
  primaryConversationSummary: ConversationRecord;
  secondaryConversationSummary: ConversationRecord;
  onClose: () => void;
  user: UserSummary;
  hasDifferentParticipants: boolean;
  isCrossUserMergeEnabled: boolean;
  onShowMergeConfirmationModal: () => void;
  onMergeConversation: (
    primary: ConversationRecord,
    secondary: ConversationRecord,
  ) => Promise<void>;
  wasSwappedForWhatsApp?: boolean;
}

export default class MergeInto extends Component<Args> {
  @service declare intl: IntlService;
  @service notificationsService: any;
  @service declare snackbar: Snackbar;
  @service declare commandK: CommandKService;
  @service declare intercomEventService: any;
  @service declare session: Session;

  @tracked isDisabled = false;

  get shouldShowConfirmationModal(): boolean {
    return this.args.hasDifferentParticipants && this.args.isCrossUserMergeEnabled;
  }

  @action async onConfirm() {
    if (this.isDisabled) {
      return;
    }

    if (this.shouldShowConfirmationModal) {
      this.args.onShowMergeConfirmationModal();
      return;
    } else {
      this.isDisabled = true;

      try {
        await this.args.onMergeConversation(
          this.args.primaryConversationSummary,
          this.args.secondaryConversationSummary,
        );
      } catch (error) {
        let response = await error.response.json();
        this.snackbar.notifyError(response.errors);
      }
    }

    this.args.onClose();
    this.commandK.hide();
  }

  @action onClose() {
    this.args.onClose();
    this.intercomEventService.trackAnalyticsEvent({
      action: 'exit',
      object: 'confirmation_merge_preview_modal',
      section: 'conversation_merge',
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::CommandK::MergeInto::MergeInto': typeof MergeInto;
    'inbox2/command-k/merge-into/merge-into': typeof MergeInto;
  }
}
